import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Box,
  Wrench,
  AlertTriangle,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  LogOut,
  User,
  Menu,
  Wind,
  Sun,
  Battery,
  Zap,
  MapPin,
  ClipboardCheck,
  Shield,
  Users,
  Building2,
  BriefcaseBusiness,
  FolderTree,
  FileText,
  Receipt,
  Activity,
  PlugZap,
  Layers,
} from 'lucide-react';
import NotificationCenter from '@/components/notifications/NotificationCenter';

const getNavItems = (userRole, enabledModules = []) => {
  const isAdmin = ['superadmin', 'platform_admin'].includes(userRole);
  
  // Base dashboard is always visible
  const baseItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' }
  ];

  // EAM Module
  if (isAdmin || enabledModules.includes('eam')) {
    baseItems.push({ icon: Box, label: 'Assets', path: '/assets' });
    baseItems.push({ icon: Wrench, label: 'Work Orders', path: '/work-orders' });
    baseItems.push({ icon: Layers, label: 'Components', path: '/components' });
    baseItems.push({ icon: AlertTriangle, label: 'Alerts', path: '/alerts' });
    if (isAdmin || enabledModules.includes('spare_parts')) {
      baseItems.push({ icon: Box, label: 'Spare Parts', path: '/spare-parts' });
    }
  }

  // Site Ops Module
  if (isAdmin || enabledModules.includes('site_ops')) {
    baseItems.push({ icon: MapPin, label: 'Site Operations', path: '/site-operations' });
  }

  // Action Plans Module
  if (isAdmin || enabledModules.includes('action_plans')) {
    baseItems.push({ icon: ClipboardCheck, label: 'Action Plans', path: '/action-plans' });
  }

  // Analytics Module
  if (isAdmin || enabledModules.includes('analytics')) {
    baseItems.push({ icon: BarChart3, label: 'Analytics', path: '/analytics' });
  }

  // Commercial Module
  if (isAdmin || enabledModules.includes('commercial')) {
    baseItems.push({ icon: FileText, label: 'Commercial', path: '/commercial' });
  }

  // Finance Module
  if (isAdmin || enabledModules.includes('finance')) {
    baseItems.push({ icon: Receipt, label: 'Finance', path: '/finance' });
  }

  baseItems.push({ icon: Settings, label: 'Settings', path: '/settings' });

  // Add admin items for privileged users
  if (isAdmin) {
    return [
      ...baseItems,
      { type: 'divider', label: 'Admin' },
      { icon: Shield, label: 'Admin Panel', path: '/admin' },
      { icon: Users, label: 'User Management', path: '/admin/users' },
      { icon: FolderTree, label: 'Portfolios', path: '/admin/portfolios' },
      { icon: BriefcaseBusiness, label: 'Companies', path: '/admin/companies' },
      { icon: PlugZap, label: 'Data Integrations', path: '/admin/integrations' },
      { icon: Activity, label: 'User Analytics', path: '/admin/user-analytics' },
      { icon: ClipboardCheck, label: 'Visit Templates', path: '/admin/visit-templates' },
    ];
  }

  // Org admin can access user management
  if (userRole === 'org_admin') {
    return [
      ...baseItems,
      { type: 'divider', label: 'Admin' },
      { icon: Users, label: 'User Management', path: '/admin/users' },
      { icon: Building2, label: 'Org Settings', path: '/admin/org-settings' },
      { icon: BriefcaseBusiness, label: 'Companies', path: '/admin/companies' },
      { icon: PlugZap, label: 'Data Integrations', path: '/admin/integrations' },
      { icon: ClipboardCheck, label: 'Visit Templates', path: '/admin/visit-templates' },
    ];
  }

  return baseItems;
};

const assetTypeIcons = {
  wind: Wind,
  solar: Sun,
  battery: Battery,
};

export default function DashboardLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, org, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleLabel = (role) => {
    const labels = {
      superadmin: 'Super Admin',
      platform_admin: 'Platform Admin',
      org_admin: 'Org Admin',
      org_user: 'User',
      viewer: 'Viewer',
      // Legacy roles
      executive: 'Executive',
      ops_manager: 'Operations Manager',
      technician: 'Technician',
      finance: 'Finance',
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-slate-900 text-slate-300 transition-all duration-300 hidden md:flex md:flex-col',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-slate-800 flex-shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-white">AMOM</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {getNavItems(user?.role, org?.enabled_modules).map((item, index) => {
            if (item.type === 'divider') {
              return (
                <div key={`divider-${index}`} className="pt-4 pb-2">
                  {!collapsed && (
                    <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {item.label}
                    </p>
                  )}
                </div>
              );
            }
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                  isActive
                    ? 'bg-sky-500/10 text-sky-400 border-r-2 border-sky-500'
                    : 'hover:bg-slate-800 hover:text-white'
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Asset Summary - Fixed at bottom */}
        {!collapsed && (
          <div className="flex-shrink-0 p-4 border-t border-slate-800">
            <div className="bg-slate-800 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider">Fleet Status</p>
              <div className="space-y-2">
                {Object.entries(assetTypeIcons).map(([type, Icon]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-slate-400" />
                      <span className="capitalize">{type}</span>
                    </div>
                    <span className="text-slate-400">
                      {type === 'wind' ? '45' : type === 'solar' ? '12' : '6'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile Menu Button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 rounded-lg text-white"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-toggle"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <aside
            className="w-64 h-full bg-slate-900 text-slate-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-16 items-center px-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-lg text-white">AMOM</span>
              </div>
            </div>
            <nav className="p-2 space-y-1">
              {getNavItems(user?.role, org?.enabled_modules).map((item, index) => {
                if (item.type === 'divider') {
                  return (
                    <div key={`mobile-divider-${index}`} className="pt-4 pb-2">
                      <p className="px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {item.label}
                      </p>
                    </div>
                  );
                }
                const isActive = location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all',
                      isActive
                        ? 'bg-sky-500/10 text-sky-400'
                        : 'hover:bg-slate-800 hover:text-white'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={cn('transition-all duration-300', collapsed ? 'md:ml-16' : 'md:ml-64')}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between h-full px-4 md:px-6">
            {/* Search */}
            <div className="flex-1 max-w-md ml-12 md:ml-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search assets, work orders..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-100 border-0 rounded-lg text-sm focus:ring-2 focus:ring-sky-500/20 focus:bg-white transition-all"
                  data-testid="global-search"
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <NotificationCenter />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-sky-500 text-white text-sm">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{getRoleLabel(user?.role)}</p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
